import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BsThreeDots } from "react-icons/bs";
import { useDispatch } from "react-redux";
import {
  deleteDepartment,
  openDepartment,
  openDepartmentDashboard,
} from "../../store/actions/departmentActions";
import { confirmAlert } from "react-confirm-alert"; // Import
import "react-confirm-alert/src/react-confirm-alert.css"; // Import css

const DepartmentCard = ({ department, onEdit, onDelete, onManageSubRiskCenter }) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const dispatch = useDispatch();

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const closeDropdown = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", closeDropdown);
    return () => {
      document.removeEventListener("mousedown", closeDropdown);
    };
  }, []);

  const handleDelete = (department_id) => {
    confirmAlert({
      message: "Are you sure you want to delete this department?",
      buttons: [
        {
          label: "Yes",
          onClick: () => dispatch(deleteDepartment(department_id)),
        },
        {
          label: "No",
        },
      ],
    });
  };

  return (
    <div className="group relative w-full rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-sm hover:shadow-md transition-all duration-200">
      {/* Dropdown */}
      <div className="absolute top-4 right-4" ref={dropdownRef}>
        <button
          className="w-8 h-8 flex items-center justify-center rounded-full text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#111827] transition"
          onClick={toggleDropdown}
        >
          <BsThreeDots size={20} />
        </button>

        {isDropdownOpen && (
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-[#E5E7EB] z-50 overflow-hidden">
            <button
              className="w-full text-left px-4 py-2.5 text-[13px] text-[#374151] hover:bg-[#F9FAFB]"
              onClick={() => {
                setIsDropdownOpen(false);
                onEdit(department);
              }}
            >
              {t("adminManagement.departments.menu.edit")}
            </button>

            <button
              className="w-full text-left px-4 py-2.5 text-[13px] text-[#374151] hover:bg-[#F9FAFB]"
              onClick={() => {
                setIsDropdownOpen(false);
                // change this function name to whatever you use for navigation
                onManageSubRiskCenter(department);
              }}
            >
              {t("adminManagement.departments.menu.manageSubRiskCenter")}
            </button>

            <button
              className="w-full text-left px-4 py-2.5 text-[13px] text-[#374151] hover:bg-[#F9FAFB]"
              onClick={() => {
                dispatch(openDepartmentDashboard(department.department_id));
                setIsDropdownOpen(false);
              }}
            >
              {t("adminManagement.departments.menu.stats")}
            </button>

            <button
              className="w-full text-left px-4 py-2.5 text-[13px] text-[#DC2626] hover:bg-[#FEF2F2]"
              onClick={() => {
                setIsDropdownOpen(false);
                onDelete(department);
              }}
            >
              {t("adminManagement.departments.menu.delete")}
            </button>
          </div>
        )}
      </div>

      {/* Header */}
      <div className="pr-10">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-[16px] font-semibold text-[#111827] truncate">
            {department.name || "Unnamed Department"}
          </h3>

          {department.__type === "compliance" && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F3F4F6] text-[#6B7280] border border-[#E5E7EB]">
              Compliance
            </span>
          )}
        </div>

        <p className="text-[12px] text-[#6B7280]">
          {department.location || t("adminManagement.departments.locationNotSpecified")}
        </p>
      </div>

      {/* Details */}
      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-[#F9FAFB] border border-[#EEF0F3] px-3 py-2">
          <div className="text-[11px] text-[#9CA3AF] mb-1">{t("adminManagement.departments.riskAppetite")}</div>
          <div
            className={`text-[13px] font-semibold ${department.risk_appetite === "Hungry"
                ? "text-[#BB133E]"
                : department.risk_appetite === "Open"
                  ? "text-[#D6606E]"
                  : department.risk_appetite === "Cautious"
                    ? "text-[#D97706]"
                    : department.risk_appetite === "Minimalist"
                      ? "text-[#00AFB9]"
                      : "text-[#0081A7]"
              }`}
          >
            {department.risk_appetite || t("adminManagement.departments.unknown")}
          </div>
        </div>

        <div className="rounded-xl bg-[#F9FAFB] border border-[#EEF0F3] px-3 py-2">
          <div className="text-[11px] text-[#9CA3AF] mb-1">{t("adminManagement.departments.personnel")}</div>
          <div className="text-[13px] font-semibold text-[#111827]">
            {department.employees || "0"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartmentCard;
